import React, { FC, useEffect, useState } from "react";
import { BiChevronLeft } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADD_CAR_IMAGES,
  DELETE_CAR_IMAGES,
  UPDATE_CAR,
  VEHICLE_CAPACITIES,
  VEHICLE_COLORS,
  VEHICLE_TYPES,
} from "../../apollo/operations";
import { Calendar } from "react-date-range";
import { enUS } from "date-fns/locale";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; //
import {
  clearVehicleToEdit,
  setVehicleToEdit,
} from "../../redux/slices/vehicleFilterSlice";
import { Range, DateRangePicker, RangeKeyDict } from "react-date-range";
import { getDatesInRange } from "../../utils/daysBetweenDates";
import { handleIPFSUpload } from "../../api/pinata";
import { RiDeleteBin5Line } from "react-icons/ri";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import { IoCloudUploadOutline } from "react-icons/io5";
import { MoonLoader } from "react-spinners";
import { CarImageType } from "../../types";
import { showToast } from "../../utils/showToast";
import Loading from "../loading/Loading";
import { stripHtml } from "../../utils/splitStringByComma";

interface EditVehicleProps {
  onEditingCompleted: () => void;
}

const EditVehicle: FC<EditVehicleProps> = ({ onEditingCompleted }) => {
  const vehicleToEdit = useSelector(
    (state: RootState) => state.vehicleFilter.vehicleToEdit
  );

  const [imageUploadLoading, setImageUploadLoading] = useState<boolean>(false);
  const [insuranceUploadLoading, setInsuranceUploadLoading] =
    useState<boolean>(false);

  const [images, setImages] = useState<CarImageType[] | undefined>(
    vehicleToEdit?.carImages?.filter(
      (image: { imageUrl: string; videoUrl: string; id: string }) => {
        return image.imageUrl;
      }
    )
    // .map((image: { imageUrl: string }) => {
    //   return image.imageUrl;
    // })
  );

  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);

  const [imagesToAdd, setImagesToAdd] = useState<string[]>([]);

  const handleInsuranceImageUpload = async (file: File) => {
    try {
      const data = await handleIPFSUpload(file);
      return data;
    } catch (error) {
      console.log(error);
      setInsuranceUploadLoading(false);
      return null;
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      const data = await handleIPFSUpload(file);
      return data;
    } catch (error) {
      console.log(error);
      setImageUploadLoading(false);
      return null;
    }
  };

  const handleAddInsuranceImage = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    if (e.target.files) {
      setInsuranceUploadLoading(true);
      const res = await handleInsuranceImageUpload(e.target.files[0]);

      if (!res?.IpfsHash) return;

      dispatch(setVehicleToEdit({ insuranceImage: res?.IpfsHash }));
      setInsuranceUploadLoading(false);
    }
  };

  const handleAddImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files) {
      setImageUploadLoading(true);
      const res = await handleFileUpload(e.target.files[0]);

      if (!res?.IpfsHash) return;

      const imgObj = {
        id: "",
        videoUrl: "",
        imageUrl: res?.IpfsHash,
      };

      const urls = images ? [...images, imgObj] : [imgObj];

      setImages(urls);
      setImagesToAdd([...imagesToAdd, res?.IpfsHash]);
      setImageUploadLoading(false);
    }
  };

  const handleRemoveImage = (image: CarImageType) => {
    const urls = images?.filter((data) => data.id !== image.id);
    setImagesToDelete([...imagesToDelete, image.id]);
    setImages(urls);
  };

  const dispatch = useDispatch();
  const [calendarDirection, setCalendarDirection] = React.useState<
    "horizontal" | "vertical"
  >("horizontal");

  const [state, setState] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [maxDate, setMaxDate] = useState<Date>(new Date());

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // getMonth() returns a zero-based value (where 0 indicates the first month of the year)
  const formattedMonth = `${currentYear}-${currentMonth
    .toString()
    .padStart(2, "0")}`; // padStart ensures the month is always 2 digits

  const {
    data: capacity,
    error: capacityError,
    loading: capacityLoading,
  } = useQuery(VEHICLE_CAPACITIES);

  const {
    data: colors,
    error: colorsError,
    loading: colorsLoading,
  } = useQuery(VEHICLE_COLORS);

  const {
    data: types,
    error: typesError,
    loading: typesLoading,
  } = useQuery(VEHICLE_TYPES);

  const [addCarImage, { loading: addImageLoading, error: addImageError }] =
    useMutation(ADD_CAR_IMAGES, {
      onCompleted: (data) => {
        // console.log(data.createCarImage);
      },
      onError: (error) => {
        console.log(error.networkError, error.message, error.message);
      },
    });

  const [
    deleteCarImage,
    { loading: deleteImageLoading, error: deleteImageError },
  ] = useMutation(DELETE_CAR_IMAGES, {
    onCompleted: (data) => {
      // console.log(data.createCarImage);
    },
    onError: (error) => {
      console.log(error.networkError, error.message, error.message);
    },
  });

  const [
    updateCar,
    { data: updateData, error: updateError, loading: updateLoading },
  ] = useMutation(UPDATE_CAR, {
    variables: {
      where: {
        id: vehicleToEdit?.id,
      },
      data: {
        color: vehicleToEdit?.color,
        millagePerCharge: vehicleToEdit?.millagePerCharge,
        pricePerHour: vehicleToEdit?.pricePerHour,
        vehiclePlateNumber: vehicleToEdit?.vehiclePlateNumber,
        registrationInfo: vehicleToEdit?.registrationInfo,
        insuranceProvider: vehicleToEdit?.insuranceProvider,
        insuranceProviderNumber: vehicleToEdit?.insuranceProviderNumber,
        policyNumber: vehicleToEdit?.policyNumber,
        insuranceEffectiveDate:
          vehicleToEdit?.insuranceEffectiveDate?.toString(),
        insuranceExpDate: vehicleToEdit?.insuranceExpDate?.toString(),
        description: vehicleToEdit?.description,
        insuranceImage: vehicleToEdit?.insuranceImage,
        availability: vehicleToEdit?.availability,
      },
    },
    onCompleted: (data) => {
      // console.log(data);
      if (imagesToAdd?.length > 0) {
        imagesToAdd.forEach((url) => {
          addCarImage({
            variables: {
              data: {
                carId: {
                  id: vehicleToEdit?.id,
                },
                imageUrl: url,
                videoUrl: "",
              },
            },
          });
        });
      }
      if (imagesToDelete?.length > 0) {
        imagesToDelete
          .filter((id) => id !== "")
          .forEach((id) => {
            deleteCarImage({
              variables: {
                where: {
                  id,
                },
              },
            });
          });
      }

      showToast("Vehicle Updated Successfully", "success");
      dispatch(clearVehicleToEdit());
      onEditingCompleted();
    },
    onError: (error) => {
      showToast(error?.message, "error");
    },
  });

  const handleUpdate = async () => {
    // Validate description
    if (
      !vehicleToEdit?.description ||
      vehicleToEdit?.description?.length < 32
    ) {
      return showToast("Description must be at least 32 characters", "error");
    }

    // Validate images
    if (!images?.length || images?.length < 6) {
      return showToast("Please upload at least 6 images", "error");
    }

    // Validate price
    if (
      !vehicleToEdit?.pricePerHour ||
      isNaN(vehicleToEdit?.pricePerHour) ||
      vehicleToEdit?.pricePerHour <= 0
    ) {
      return showToast("Please enter a valid price per hour", "error");
    }

    try {
      await updateCar();
    } catch (error) {
      showToast(error as string, "error");
    }
  };

  const [effectiveDate, setEffectiveDate] = useState<string>(
    `${
      vehicleToEdit?.insuranceEffectiveDate
        ? new Date(vehicleToEdit?.insuranceEffectiveDate)?.toLocaleDateString(
            "en-US"
          )
        : "Select Date"
    }`
  );
  const [showEffectiveDate, setShowEffectiveDate] = useState<boolean>(false);

  const [regDate, setRegDate] = useState<Date>();

  const [expiryDate, setExpiryDate] = useState<string>(
    `${
      vehicleToEdit?.insuranceExpDate
        ? new Date(vehicleToEdit?.insuranceExpDate)?.toLocaleDateString("en-US")
        : "Select Date"
    }`
  );
  const [showExpiryDate, setShowExpiryDate] = useState<boolean>(false);

  const handleEffectiveDateSelect = (date: Date) => {
    // console.log(date);
    setShowEffectiveDate(false);
    dispatch(
      setVehicleToEdit({
        insuranceEffectiveDate: date.toISOString(),
      })
    );
    setEffectiveDate(date.toLocaleDateString("en-US"));
  };

  const handleExpiryDateSelect = (date: Date) => {
    setShowExpiryDate(false);
    dispatch(
      setVehicleToEdit({
        insuranceExpDate: date.toISOString(),
      })
    );
    setExpiryDate(date.toLocaleDateString("en-US"));

    // console.log(date);
  };

  const handleRangeSelect = async (item: RangeKeyDict) => {
    // console.log("selection", item.selection);
    if (item.selection) {
      try {
        // console.log("selection", item.selection);
        // const dates = `${item.selection.startDate.toISOString()},${item.selection.endDate.toISOString()}`;
        // console.log(dates);

        const arr = getDatesInRange(
          item.selection.startDate!,
          item.selection.endDate!
        );

        // console.log(arr);

        dispatch(setVehicleToEdit({ availability: arr }));

        setState([item.selection]);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 780) {
        setCalendarDirection("vertical");
      } else if (
        window.innerWidth > 780 &&
        calendarDirection !== "horizontal"
      ) {
        setCalendarDirection("horizontal");
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, [calendarDirection]);

  useEffect(() => {
    if (vehicleToEdit && vehicleToEdit.registrationInfo) {
      const registrationInfo = vehicleToEdit.registrationInfo;

      // Check the format of the registrationInfo
      let year, month;
      if (registrationInfo.includes("-")) {
        // Format is yyyy-mm
        [year, month] = registrationInfo.split("-").map(Number);
      } else if (registrationInfo.includes("/")) {
        // Format is mm/yyyy
        [month, year] = registrationInfo.split("/").map(Number);
      }

      // Set the registration date
      setRegDate(new Date(year as number, (month as number) - 1, 1));
    }
  }, [vehicleToEdit]);

  useEffect(() => {
    if (vehicleToEdit) {
      const insuranceMaxDate = new Date(
        vehicleToEdit.insuranceExpDate ?? new Date().toISOString()
      );

      const regMaxDate = new Date(
        (regDate as Date) ?? new Date().toISOString()
      );

      setMaxDate(
        new Date(Math.max(insuranceMaxDate.getTime(), regMaxDate.getTime()))
      );
    }
  }, [vehicleToEdit, regDate]);

  if (
    capacityLoading ||
    colorsLoading ||
    typesLoading ||
    updateLoading ||
    addImageLoading ||
    deleteImageLoading
  ) {
    return (
      <Loading
        loading={
          capacityLoading ||
          colorsLoading ||
          typesLoading ||
          updateLoading ||
          addImageLoading ||
          deleteImageLoading
        }
      />
    );
  }
  return (
    <div className=" bg-white/50 flex flex-col gap-5 w-full max-w-[80%] mx-auto py-10 md:py-20 ">
      <div className=" w-full flex items-center ">
        <div
          onClick={() => dispatch(clearVehicleToEdit())}
          className="flex items-center gap-2 cursor-pointer"
        >
          <BiChevronLeft className=" text-[#3D475C] text-2xl" />
          <span className=" font-Jakarta font-normal text-base text-darkGreen">
            Back
          </span>
        </div>
        <span className=" font-Jakarta w-full text-center text-base font-semibold text-midGreen">
          Edit Vehicle Details
        </span>
      </div>
      <div className="container bg-white shadow-md rounded-[10px] min-h-screen p-5 md:p-10 flex flex-col gap-7">
        {/* Info Alert */}
        {/* <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <div className="flex items-start">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                You can now update your vehicle's price per hour! Set a
                competitive rate to attract more renters.
              </p>
            </div>
          </div>
        </div> */}

        {/* Vehicle Details Section */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-midGreen mb-6">
            General Information
          </h2>
          <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
            <div className="flex lg:w-1/2 flex-col">
              <label
                htmlFor="vin"
                className="block font-medium text-gray-700 mb-1"
              >
                VIN
              </label>
              <input
                id="vin"
                type="text"
                className="w-full border border-[#C4BFC6] cursor-not-allowed lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
                placeholder="VIN"
                disabled
                value={vehicleToEdit?.vin ?? ""}
                required
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setVehicleToEdit({
                      vin: e.target.value,
                    })
                  );
                }}
              />
            </div>
            <div className="flex lg:w-1/2 flex-col">
              <label
                htmlFor="brand"
                className="block font-medium text-gray-700 mb-1"
              >
                Vehicle Brand
              </label>
              <input
                id="brand"
                type="text"
                className="w-full border border-[#C4BFC6] lg:outline-none cursor-not-allowed lg:bg-[#E5F9EF] rounded-md p-3"
                placeholder="Vehicle Brand"
                disabled
                required
                value={vehicleToEdit?.make ?? ""}
                min={formattedMonth}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setVehicleToEdit({
                      make: e.target.value,
                    })
                  );
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2  flex-col">
            <label
              htmlFor="model"
              className="block font-medium text-gray-700 mb-1"
            >
              Model
            </label>
            <input
              id="model"
              type="text"
              className="w-full border border-[#C4BFC6] lg:outline-none cursor-not-allowed lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Model"
              disabled
              required
              value={vehicleToEdit?.model ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    model: e.target.value,
                  })
                );
              }}
            />
          </div>
          <div className="flex lg:w-1/2  flex-col">
            <label
              htmlFor="vehicleType"
              className="block font-medium text-gray-700 mb-1"
            >
              Vehicle Type
            </label>
            <Dropdown
              options={
                types ? types.vehicleTypes?.map((typ: any) => typ?.type) : []
              }
              onChange={(item) => {
                console.log(item);
                dispatch(
                  setVehicleToEdit({
                    vehicleType: item.value,
                  })
                );
              }}
              value={vehicleToEdit?.vehicleType ?? ""}
              disabled
              placeholder="Select an option"
              className="w-full border h-12 border-[#C4BFC6] lg:outline-none cursor-not-allowed lg:bg-[#E5F9EF] rounded-md"
              controlClassName="accent-bg h-full"
            />
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Color
            </label>
            {/* <input
                type="text"
                className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
                placeholder="Color"
                required
                value={vehicleToEdit?.color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setVehicleToEdit({
                      color: e.target.value,
                    })
                  );
                }}
              /> */}
            <Dropdown
              options={
                colors
                  ? colors.vehicleColors?.map((col: any) => col?.color)
                  : []
              }
              onChange={(item) => {
                console.log(item);
                dispatch(
                  setVehicleToEdit({
                    color: item.value,
                  })
                );
              }}
              value={vehicleToEdit?.color ?? ""}
              placeholder="Select an option"
              className="w-full border h-12 border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-"
              controlClassName="accent-bg h-full"
            />
          </div>
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Year
            </label>
            <input
              type="text"
              className="w-full border border-[#C4BFC6] lg:outline-none cursor-not-allowed lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Year"
              required
              disabled
              value={vehicleToEdit?.year ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    year: e.target.value,
                  })
                );
              }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Seating Capacity
            </label>
            {/* <input
                type="number"
                className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
                placeholder="Seating Capacity"
                required
                value={vehicleToEdit?.capacity}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setVehicleToEdit({
                      capacity: Number(e.target.value),
                    })
                  );
                }}
              /> */}
            <Dropdown
              options={
                capacity
                  ? capacity.vehicleCapacities?.map((cap: any) => cap?.capacity)
                  : []
              }
              onChange={(item) => {
                // console.log(item);
                dispatch(
                  setVehicleToEdit({
                    capacity: Number(item.value),
                  })
                );
              }}
              value={
                vehicleToEdit?.capacity ? `${vehicleToEdit?.capacity}` : ""
              }
              disabled
              placeholder="Select an option"
              className="w-full border h-12 border-[#C4BFC6] lg:outline-none cursor-not-allowed lg:bg-[#E5F9EF] rounded-md p-"
              controlClassName="accent-bg h-full"
            />
          </div>
          {/* <div className="flex lg:w-1/2  flex-col">
              <label htmlFor="" className=" block ">
                Seats
              </label>
              <input
                type="number"
                className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
                placeholder="Seats"
                required
                value={vehicleToEdit?.seats}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setVehicleToEdit({
                      seats: Number(e.target.value),
                    })
                  );
                }}
              />
            </div> */}
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Mileage per charge
            </label>
            <input
              type="number"
              className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Mileage per charge"
              required
              value={vehicleToEdit?.millagePerCharge ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    millagePerCharge: Number(e.target.value),
                  })
                );
              }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2 flex-col">
            <label
              htmlFor="pricePerDay"
              className="block font-medium text-gray-700"
            >
              Price per Day ($)
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="pricePerHour"
                className="w-full border border-[#C4BFC6] pl-7 lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
                placeholder="0"
                min="1"
                step="1"
                required
                value={vehicleToEdit?.pricePerHour ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setVehicleToEdit({
                      pricePerHour: parseFloat(e.target.value),
                    })
                  );
                }}
              />
            </div>
          </div>
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className="block font-medium text-gray-700">
              Plate Number
            </label>
            <input
              type="text"
              className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Number Plate"
              required
              value={vehicleToEdit?.vehiclePlateNumber ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    vehiclePlateNumber: e.target.value,
                  })
                );
              }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className="block font-medium text-gray-700">
              Registration Good Thru
            </label>
            <input
              type="month"
              className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Registration Information"
              required
              value={vehicleToEdit?.registrationInfo ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    registrationInfo: e.target.value,
                  })
                );
              }}
            />
          </div>
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className="block font-medium text-gray-700">
              Insurance Provider
            </label>
            <input
              type="text"
              className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Insurance Provider"
              required
              value={vehicleToEdit?.insuranceProvider}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    insuranceProvider: e.target.value,
                  })
                );
              }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2 flex-col">
            <label htmlFor="" className=" block ">
              Insurance Provider Number
            </label>
            <input
              type="text"
              className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Insurance Provider Number"
              value={vehicleToEdit?.insuranceProviderNumber}
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    insuranceProviderNumber: e.target.value,
                  })
                );
              }}
            />
          </div>
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Policy Provider Number
            </label>
            <input
              type="text"
              className="w-full border border-[#C4BFC6] lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3"
              placeholder="Policy Provider Number"
              required
              value={vehicleToEdit?.policyNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  setVehicleToEdit({
                    policyNumber: e.target.value,
                  })
                );
              }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Policy Effective Date
            </label>
            <div
              className="w-full border border-[#C4BFC6]
                lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3 py-3 cursor-pointer"
              onClick={() => {
                setShowEffectiveDate(!showEffectiveDate);
                setShowExpiryDate(false);
              }}
            >
              {effectiveDate}
            </div>
            <div className="w-full flex justify-center ">
              {showEffectiveDate && (
                <Calendar
                  date={new Date()}
                  // minDate={new Date()}
                  onChange={handleEffectiveDateSelect}
                  locale={enUS}
                />
              )}
            </div>
          </div>
          <div className="flex lg:w-1/2  flex-col">
            <label htmlFor="" className=" block ">
              Policy Expiry Date
            </label>
            <div
              className="w-full border border-[#C4BFC6]
                lg:outline-none lg:bg-[#E5F9EF] rounded-md p-3 py-3 cursor-pointer"
              onClick={() => {
                setShowExpiryDate(!showExpiryDate);
                setShowEffectiveDate(false);
              }}
            >
              {expiryDate}
            </div>
            <div className="w-full flex justify-center ">
              {showExpiryDate && (
                <Calendar
                  date={new Date()}
                  minDate={new Date()}
                  onChange={handleExpiryDateSelect}
                  locale={enUS}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col lg:flex-row justify-between gap-[40px]">
          <div className="flex w-full flex-col">
            <label htmlFor="" className=" flex justify-between items-center ">
              Vehicle Description
              <span className="">
                {vehicleToEdit?.description?.length
                  ? stripHtml(vehicleToEdit?.description)?.length
                  : 0}{" "}
                / 512
              </span>
            </label>

            <ReactQuill
              theme="snow"
              value={vehicleToEdit?.description ?? ""}
              onChange={(value) => {
                // console.log(value.length, stripHtml(value).length);
                if (stripHtml(value).length > 512) return;
                dispatch(
                  setVehicleToEdit({
                    description: value,
                  })
                );
              }}
            />
          </div>
        </div>

        {state?.length > 0 && (
          <div className="flex flex-col gap-5">
            <label htmlFor="" className=" block ">
              Vehicle Availability
            </label>
            <DateRangePicker
              onChange={(item) => handleRangeSelect(item)}
              // showSelectionPreview={false}
              moveRangeOnFirstSelection={false}
              editableDateInputs={true}
              months={2}
              ranges={state}
              locale={enUS}
              preview={undefined}
              showPreview={false}
              rangeColors={["#215759"]}
              color="#215759"
              direction={calendarDirection}
              staticRanges={undefined}
              inputRanges={undefined}
              minDate={new Date()}
              maxDate={maxDate}
              className="w-full flex justify-start flex-wrap"
            />
          </div>
        )}

        <div className="flex flex-col gap-5">
          <label htmlFor="" className=" block ">
            Vehicle Images
          </label>
          <div className="flex flex-wrap gap-5 items-center">
            {images?.length &&
              images?.map((image, index) => (
                <div
                  className="w-[120px] h-[120px] lg:w-[200px] lg:h-[200px] relative"
                  key={index}
                >
                  <RiDeleteBin5Line
                    onClick={() => handleRemoveImage(image)}
                    className="text-white text-[20px] cursor-pointer absolute right-0 m-2"
                  />
                  <img
                    src={ipfsGatewayImage(image.imageUrl as string)}
                    alt=""
                    className=" object-cover h-full w-full"
                  />
                </div>
              ))}
            <div className="flex flex-col gap-2 border-2 border-dashed p-5 border-[#D0D5DD] rounded-xl w-[120px] h-[120px] lg:w-[200px] lg:h-[200px] justify-center items-center cursor-pointer ">
              {imageUploadLoading ? (
                <MoonLoader color="#2ff77a" size={50} />
              ) : (
                <label
                  htmlFor="imageAdd"
                  className="flex flex-col gap-2 w-full justify-center items-center cursor-pointer"
                >
                  <IoCloudUploadOutline size={25} color="#2ff77a" />

                  <p className="text-white bg-midGreen rounded-xl p-3 flex flex-col gap-2 font-Poppins text-[12px] font-normal text-center">
                    Add Image
                  </p>
                </label>
              )}
              <input
                type="file"
                accept="image/*"
                id="imageAdd"
                capture="environment"
                hidden
                onChange={(e) => {
                  handleAddImage(e);
                }}
              />
            </div>
          </div>
        </div>

        <div className=" flex flex-col gap-5">
          <label htmlFor="" className=" block ">
            Insurance Card Image
          </label>
          <div className="flex flex-wrap gap-5 items-center">
            {vehicleToEdit?.insuranceImage ? (
              <div className="w-[120px] h-[120px] lg:w-[200px] lg:h-[200px] relative">
                <RiDeleteBin5Line
                  onClick={() =>
                    dispatch(setVehicleToEdit({ insuranceImage: "" }))
                  }
                  className="text-white text-[20px] cursor-pointer absolute right-0 m-2"
                />
                <img
                  src={ipfsGatewayImage(
                    vehicleToEdit?.insuranceImage as string
                  )}
                  alt=""
                  className=" object-cover h-full w-full"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2 border-2 border-dashed p-5 border-[#D0D5DD] rounded-xl w-[120px] h-[120px] lg:w-[200px] lg:h-[200px] justify-center items-center cursor-pointer ">
                {insuranceUploadLoading ? (
                  <MoonLoader color="#2ff77a" size={50} />
                ) : (
                  <label
                    htmlFor="insuranceImage"
                    className="flex flex-col gap-2 w-full justify-center items-center cursor-pointer"
                  >
                    <IoCloudUploadOutline size={25} color="#2ff77a" />

                    <p className="text-white bg-midGreen rounded-xl p-3 flex flex-col gap-2 font-Poppins text-[12px] font-normal text-center">
                      Add Image
                    </p>
                  </label>
                )}
                <input
                  type="file"
                  accept="image/*"
                  id="insuranceImage"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    handleAddInsuranceImage(e);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between w-full mt-8 items-center">
          <button
            onClick={() => dispatch(clearVehicleToEdit())}
            className="bg-white border border-midGreen text-midGreen font-medium py-3 px-8 rounded-xl hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => handleUpdate()}
            className="bg-midGreen text-white font-medium w-[200px] py-3 px-8 rounded-xl hover:bg-green-700 transition-all flex items-center justify-center"
            disabled={updateLoading}
          >
            {updateLoading ? (
              <span className="flex items-center">
                <MoonLoader color="#ffffff" size={20} />
                <span className="ml-2">Updating...</span>
              </span>
            ) : (
              "Update Vehicle"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditVehicle;
